import { useEffect, useState, type ReactNode } from "react";

import { useTokens } from "../hooks/useTokens";
import type { DadosPiloto } from "../hooks/usePiloto";
import { corDaBoia } from "../lib/cores";
import { horaComSegundos, horaCurta, num } from "../lib/format";
import {
  ROTULO_FASE,
  faseNoInstante,
  ultimoAte,
  type Piloto,
} from "../lib/pilotos";
import { formatador, rotuloFuso } from "../lib/tempo";

/**
 * Passo do cursor e folga dos valores: `status` e a série de temperatura vêm
 * ~1/min, portanto abaixo do minuto o cursor só interpolava silêncio. A folga
 * é 2× a cadência — mais velho que isso não é "o valor em t", é ausência, e a
 * ausência mostra-se ("—").
 */
const PASSO_MS = 60_000;
const FOLGA_MS = 150_000;

/** Replay: avança 2 min de piloto por tick de 200 ms — a manhã inteira em ~40 s. */
const TICK_MS = 200;
const AVANCO_MS = 120_000;

interface Props {
  piloto: Piloto;
  dados: DadosPiloto;
  /** Instante do cursor, em ms de época. */
  instante: number;
  aoMudarInstante: (ms: number) => void;
  selecionado: string | null;
  aoSelecionar: (code: string) => void;
}

/**
 * Barra de replay do piloto, por cima do mapa.
 *
 * O cursor percorre a janela do piloto; os marcadores no mapa seguem-no e as
 * fichas mostram o valor de cada boia **naquele instante** — cada lote
 * recebido, pela ordem em que aconteceu, até ao momento em que a última se
 * calou. As marcas por baixo do cursor são os factos do dia: entrada na água
 * (círculo aberto) e último lote (quadrado) de cada boia, clicáveis para
 * saltar lá.
 */
export function PilotoBarra({
  piloto,
  dados,
  instante,
  aoMudarInstante,
  selecionado,
  aoSelecionar,
}: Props): ReactNode {
  const escuro = useTokens()["--modo"] === "dark";
  const [aTocar, setATocar] = useState(false);

  const min = Date.parse(piloto.de);
  const max = Date.parse(piloto.ate);

  useEffect(() => {
    if (!aTocar) return;
    const t = setInterval(() => {
      const proximo = Math.min(instante + AVANCO_MS, max);
      aoMudarInstante(proximo);
      if (proximo >= max) setATocar(false);
    }, TICK_MS);
    return () => clearInterval(t);
  }, [aTocar, instante, max, aoMudarInstante]);

  const percent = (ts: number): number => ((ts - min) / (max - min)) * 100;
  const dia = formatador({ day: "2-digit", month: "2-digit", year: "numeric" }).format(
    new Date(min),
  );

  return (
    <div className={dados.aCarregar ? "piloto-barra a-recarregar" : "piloto-barra"}>
      <div className="cabecalho">
        <h2>Replay do piloto</h2>
        <span className="janela">
          {dia} · {horaCurta(piloto.de)}–{horaCurta(piloto.ate)} · {rotuloFuso(new Date(max))}
        </span>
        <span className="agora">{horaComSegundos(new Date(instante).toISOString())}</span>
      </div>

      <div className="piloto-tempo">
        <button
          type="button"
          className="tocar"
          aria-pressed={aTocar}
          aria-label={aTocar ? "Pausar o replay" : "Tocar o replay"}
          onClick={() => {
            // No fim, tocar recomeça do princípio — senão o botão não fazia nada.
            if (!aTocar && instante >= max) aoMudarInstante(min);
            setATocar((a) => !a);
          }}
        >
          {aTocar ? "❚❚" : "▶"}
        </button>
        <div className="linha-tempo">
          <input
            type="range"
            min={min}
            max={max}
            step={PASSO_MS}
            value={instante}
            aria-label="Instante do replay"
            onChange={(e) => aoMudarInstante(Number(e.target.value))}
          />
          <div className="marcas">
            {piloto.boias.flatMap((b) => {
              const cor = corDaBoia(b.code, escuro);
              const marcas = [];
              if (b.imersao !== null) {
                marcas.push(
                  <button
                    key={`${b.code}-agua`}
                    type="button"
                    className="marca agua"
                    style={{ left: `${percent(Date.parse(b.imersao))}%`, borderColor: cor }}
                    title={`${b.code} entra na água — ${horaComSegundos(b.imersao)}`}
                    onClick={() => aoMudarInstante(Date.parse(b.imersao as string))}
                  />,
                );
              }
              marcas.push(
                <button
                  key={`${b.code}-fim`}
                  type="button"
                  className="marca fim"
                  style={{ left: `${percent(Date.parse(b.fim))}%`, background: cor }}
                  title={`${b.code} — último lote às ${horaComSegundos(b.fim)}`}
                  onClick={() => aoMudarInstante(Date.parse(b.fim))}
                />,
              );
              return marcas;
            })}
          </div>
        </div>
      </div>

      <div className="piloto-fichas" role="group" aria-label="Boias do piloto">
        {piloto.boias.map((b) => {
          const fase = faseNoInstante(b, instante);
          const boia = dados.por[b.code];
          const temp = ultimoAte(
            boia?.temperatura?.points ?? [],
            (p) => Date.parse(p.ts),
            instante,
            FOLGA_MS,
          );
          const status = ultimoAte(
            boia?.status ?? [],
            (p) => Date.parse(p.timestamp),
            instante,
            FOLGA_MS,
          );

          return (
            <button
              key={b.code}
              type="button"
              className="piloto-ficha"
              aria-pressed={b.code === selecionado}
              title={`Abrir o painel de ${b.code}`}
              onClick={() => aoSelecionar(b.code)}
            >
              <span className="linha1">
                <i aria-hidden="true" style={{ background: corDaBoia(b.code, escuro) }} />
                {b.code}
                <span className={fase === "morta" ? "fase morta" : "fase"}>
                  {fase === "morta" ? `morta às ${horaComSegundos(b.fim)}` : ROTULO_FASE[fase]}
                </span>
              </span>
              <span className="linha2">
                {temp?.mean == null ? "— °C" : `${num(temp.mean, 1)} °C`}
                {" · "}
                {status === null ? "— V" : `${num(status.voltage_v, 2)} V`}
              </span>
            </button>
          );
        })}
      </div>

      {dados.erro !== null && (
        <p className="aviso-erro" role="alert">
          {dados.erro}
        </p>
      )}
      <p className="nota">{piloto.nota}</p>
    </div>
  );
}
